import { makeStyles } from '@mui/styles';
import { MenuItem } from 'apollo-react';
import Accordion from 'apollo-react/components/Accordion';
import AccordionDetails from 'apollo-react/components/AccordionDetails';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import Grid from 'apollo-react/components/Grid';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import Typography from 'apollo-react/components/Typography';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  setRepositoryPropValue,
  validateRepository,
} from '../../actions/RepositoryConfigurationActions';
import { REPOSITORY_TYPES } from '../../constants/RepositoryConfigurationConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import {
  getErrorByProp,
  getRepositoryProp,
} from '../../selectors/RepositoryConfigurationSelectors';

const useStyles = makeStyles({
  accordionDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  column: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 700,
    marginTop: 5,
  },
  row: {
    display: 'flex',
    width: '100%',
    '@media (max-width: 480px)': {
      flexDirection: 'column',
    },
  },
  rowLabel: {
    width: 200,
    paddingTop: 8,
    marginRight: 20,
    alignItems: 'center',
  },
  repositoryName: {
    marginTop: 8,
  },
  rowControl: {
    flex: 1,
    minWidth: 300,
    alignItems: 'center',
    '& div': {
      margin: 0,
    },
    '@media (max-width: 480px)': {
      paddingTop: 8,
    },
    "& label[class*='MuiInputLabel-root'] span[class*='MuiFormLabel-asterisk']": {
      top: '3px !important',
      left: '-15px !important',
    },
  },
});

const Details = ({ ref }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const { search } = useLocation();

  const repositoryName = useSelector((state) => getRepositoryProp(state, { propName: 'name' }));
  const repositoryType = useSelector((state) => getRepositoryProp(state, { propName: 'type' }));
  const repositoryNameValError = useSelector(
    (state) => getErrorByProp(state, { prop: 'repositoryName' }),
    equalityFn
  );

  const [repositoryNameValue, setRepositoryNameValue] = useState(repositoryName);

  const { isNew } = qs.parse(search) || {};

  useEffect(() => {
    setRepositoryNameValue(repositoryName);
  }, [repositoryName]);

  const handleNameChange = () => {
    if (repositoryNameValue !== repositoryName) {
      dispatch(setRepositoryPropValue({ prop: 'name', value: repositoryNameValue.trim() }));
      repositoryNameValError && dispatch(validateRepository());
    }
  };

  return (
    <Accordion defaultExpanded style={{ marginBottom: 10 }} ref={ref}>
      <AccordionSummary>
        <Typography>{translateString('Repository Details')}</Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Grid container spacing={1} flexDirection="column">
          <Grid item>
            <div className={classes.column}>
              <div className={classes.row}>
                <div className={classes.rowLabel}>
                  <Typography>{translateString('Document Repository Name')}</Typography>
                </div>
                <div className={classes.rowControl}>
                  {isNew ? (
                    <TextField
                      fullWidth
                      asteriskPosition="before"
                      required
                      value={repositoryNameValue}
                      style={{ maxWidth: 400 }}
                      onChange={({ target }) => setRepositoryNameValue(target.value)}
                      onBlur={handleNameChange}
                      error={!!repositoryNameValError}
                      helperText={repositoryNameValError ? repositoryNameValError.message : null}
                      inputProps={{
                        'aria-label': translateString('Document Repository Name'),
                      }}
                    />
                  ) : (
                    <Typography className={classes.repositoryName}>{repositoryName}</Typography>
                  )}
                </div>
              </div>
            </div>
          </Grid>
          <Grid item>
            <div className={classes.column}>
              <div className={classes.row}>
                <div className={classes.rowLabel}>
                  <Typography>{translateString('Type')}</Typography>
                </div>
                <div className={classes.rowControl}>
                  <Select
                    value={repositoryType}
                    onChange={({ target: { value } }) =>
                      dispatch(setRepositoryPropValue({ prop: 'type', value }))
                    }
                    style={{ paddingLeft: isNew ? 12 : 0, maxWidth: 400 }}
                    canDeselect={false}
                    fullWidth
                  >
                    {REPOSITORY_TYPES.map(({ label, value }) => (
                      <MenuItem value={value} key={value}>
                        {translateString(label)}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default Details;
